import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
    type Dispatch,
    type ReactNode,
    type SetStateAction,
} from "react";
import {twJoin} from "tailwind-merge";
import {readStoredAsidePanelWidth} from "~/utilities/asidePanelSizing";

export type AsidePanelState = {
    isOpen: boolean;
    isFullscreen: boolean;
    width: number;
    isResizing: boolean;
};

type AsidePanelContextValue = {
    state: AsidePanelState;
    portalTarget: HTMLElement | null;
    registerPortalTarget: (element: HTMLElement | null) => void;
    setIsOpen: Dispatch<SetStateAction<boolean>>;
    setIsFullscreen: Dispatch<SetStateAction<boolean>>;
    setWidth: Dispatch<SetStateAction<number>>;
    setIsResizing: Dispatch<SetStateAction<boolean>>;
};

const AsidePanelContext = createContext<AsidePanelContextValue | null>(null);

export function AsidePanelProvider({children}: {children: ReactNode}) {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
    const [width, setWidth] = useState<number>(() => readStoredAsidePanelWidth());
    const [isResizing, setIsResizing] = useState<boolean>(false);
    const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);

    const registerPortalTarget = useCallback((element: HTMLElement | null) => {
        setPortalTarget((current) => (element !== null ? element : current));
    }, []);

    const value = useMemo<AsidePanelContextValue>(
        () => ({
            state: {
                isOpen: isOpen,
                isFullscreen: isFullscreen,
                width: width,
                isResizing: isResizing,
            },
            portalTarget: portalTarget,
            registerPortalTarget: registerPortalTarget,
            setIsOpen: setIsOpen,
            setIsFullscreen: setIsFullscreen,
            setWidth: setWidth,
            setIsResizing: setIsResizing,
        }),
        [isFullscreen, isOpen, isResizing, portalTarget, registerPortalTarget, width],
    );

    return <AsidePanelContext.Provider value={value}>{children}</AsidePanelContext.Provider>;
}

/**
 * Nested aside scope for content that already lives inside the page aside column
 * (e.g. the Events tab of the entity profile aside). Descendant `AsidePanelPortal`s
 * render as an overlay filling this container instead of stacking into the page
 * aside slot, which the profile itself occupies.
 */
export function NestedAsidePanelScope({children, className}: {children: ReactNode; className?: string}) {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
    const [width, setWidth] = useState<number>(() => readStoredAsidePanelWidth());
    const [isResizing, setIsResizing] = useState<boolean>(false);
    const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);

    const registerPortalTarget = useCallback((element: HTMLElement | null) => {
        setPortalTarget((current) => (element !== null ? element : current));
    }, []);

    const value = useMemo<AsidePanelContextValue>(
        () => ({
            state: {
                isOpen: isOpen,
                isFullscreen: isFullscreen,
                width: width,
                isResizing: isResizing,
            },
            portalTarget: portalTarget,
            registerPortalTarget: registerPortalTarget,
            setIsOpen: setIsOpen,
            setIsFullscreen: setIsFullscreen,
            setWidth: setWidth,
            setIsResizing: setIsResizing,
        }),
        [isFullscreen, isOpen, isResizing, portalTarget, registerPortalTarget, width],
    );

    return (
        <AsidePanelContext.Provider value={value}>
            <div className={twJoin("relative h-full min-h-0 min-w-0 w-full overflow-hidden", className)}>
                {children}
                <div
                    ref={registerPortalTarget}
                    className={twJoin(
                        "absolute inset-0 z-40 min-h-0 min-w-0 overflow-hidden bg-ds-elevation-surface-current",
                        isOpen !== true && "hidden",
                    )}
                />
            </div>
        </AsidePanelContext.Provider>
    );
}

export function useAsidePanelContext(): AsidePanelContextValue {
    const context = useContext(AsidePanelContext);
    if (context === null) {
        throw new Error("useAsidePanelContext must be used within AsidePanelProvider");
    }
    return context;
}

export function useAsidePanelContextSafe(): AsidePanelContextValue | null {
    return useContext(AsidePanelContext);
}
